import React, { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import {
  DateField,
  InputField,
  MessageBox,
  SwitchField,
} from 'app/components/Core';
import { SingleTrackExportFormValues } from '../types';
import { defaultValues } from '../constants';
import { formatSingleTrackExportData } from '../utils';
import { SingleTrackReleaseExportProps } from './types';
import { useSingleTrackReleaseExportForm } from './hooks';

export const SingleTrackReleaseExport: React.FC<SingleTrackReleaseExportProps> = ({
  releaseTitle,
  releaseExportLink,
  connectionGuid,
  isDisabled,
  exportRelease,
  checkConnectionStatus,
  afterExport,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [connectionId, setConnectionId] = useState<string | undefined>(
    connectionGuid
  );
  const [formSubmitDisabled, setFormSubmitDisabled] = useState(false);
  const [openDialogDisabled, setOpenDialogDisabled] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    register,
    setValue,
    formState: { isValid, isDirty },
  } = useSingleTrackReleaseExportForm({
    releaseExportLink,
    releaseTitle,
  });

  const handleOpen = async () => {
    setOpenDialogDisabled(true);
    const renewedConnectionGuid = await checkConnectionStatus('SoundCloud');
    setConnectionId(renewedConnectionGuid);
    onOpen();
    setOpenDialogDisabled(false);
  };

  const handleClose = () => {
    onClose();
    reset({
      ...defaultValues,
      title: releaseTitle,
    });
  };

  const onSubmit = async (values: SingleTrackExportFormValues) => {
    setFormSubmitDisabled(true);
    try {
      const renewedConnectionGuid = await checkConnectionStatus('SoundCloud');
      const data = await exportRelease(
        formatSingleTrackExportData(values, renewedConnectionGuid)
      );

      handleClose();

      if (afterExport) {
        afterExport('SoundCloud', data.guid as string, renewedConnectionGuid);
      }
    } catch (e: any) {
      // Currently no handler needed
    } finally {
      setFormSubmitDisabled(false);
    }
  };

  useEffect(() => {
    setValue('title', releaseTitle);
  }, [releaseTitle, setValue]);

  return (
    <>
      <Button
        size="sm"
        isDisabled={isDisabled || openDialogDisabled}
        onClick={handleOpen}
      >
        Export release
      </Button>
      <Modal
        size="md"
        isOpen={isOpen}
        onClose={handleClose}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent data-testid="dialog" sx={{ minH: 280 }}>
          <ModalHeader data-testid="dialog-title" title="Export Release">
            Export Release
          </ModalHeader>
          <ModalBody data-testid="dialog-content">
            <VStack spacing={4} as="form">
              <InputField
                label="Title"
                type="text"
                isDisabled
                overflow="hidden"
                textOverflow="ellipsis;"
                {...register('title')}
                data-testid="title"
              />
              <SimpleGrid width="full" columns={2} spacing={4}>
                <SwitchField
                  label="Private"
                  labelProps={{
                    htmlFor: 'isPrivate',
                  }}
                  data-testid="private-label"
                  {...register('isPrivate')}
                />
                <SwitchField
                  label="Downloadable track"
                  labelProps={{
                    htmlFor: 'isDownloadable',
                  }}
                  data-testid="downloadable-label"
                  {...register('isDownloadable')}
                />
                <DateField<SingleTrackExportFormValues>
                  control={control}
                  name="releaseExportDate"
                  label="Release Date"
                  dataTestId="release-export-date"
                  shouldSaveInvalidDate
                />
                <SwitchField
                  label="Enable app playback"
                  labelProps={{
                    htmlFor: 'isStreamable',
                  }}
                  tooltipProps={{
                    label: 'Make playable outside of SoundCloud and its apps.',
                    'data-testid': 'streamable-tooltip',
                  }}
                  data-testid="streamable-label"
                  {...register('isStreamable')}
                />
              </SimpleGrid>
              {!connectionId && (
                <MessageBox
                  boxProps={{
                    mb: 4,
                    w: 'full',
                    'data-testid': 'info-message',
                  }}
                >
                  You need to connect to SoundCloud to export
                </MessageBox>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Stack spacing={2} direction="row">
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit(onSubmit)}
                isDisabled={(isDirty && !isValid) || formSubmitDisabled}
              >
                {connectionId ? 'Export' : 'Connect & Export'}
              </Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
